import { qualifymeSupportEmail } from "../../../Utils/constants";

export const QUESTION_TYPES = {
  MCQ: "mcq",
  DESCRIPTIVE: "descriptive",
  CODING: "coding",
  FILE_UPLOAD: "fileUpload",
} as const;

export const SESSION_STATUS = {
  PENDING: "pending",
  COMPLETED: "completed",
  IN_PROGRESS: "in_progress",
  TERMINATED: "terminated",
} as const;

export const VIOLATION_TYPES = {
  FULLSCREEN_EXIT: "fullscreen_exit",
  TAB_SWITCH: "tab_switch",
  COPY_PASTE: "copy_paste",
  DEVICE_CHANGE: "device_change",
  REFRESH_ATTEMPT: "refresh_attempt",
  SCREEN_RECORDING_STOPPED: "screen_recording_stopped",
} as const;

export const ACTIVITY_LOG_EVENTS = {
  FULLSCREEN_EXIT: "fullscreen_exit",
  KEYBOARD_SHORTCUT: "keyboard_shortcut",
  CURSOR_FOCUS_SWITCH: "cursor_focus_switch",
  MULTIPLE_TABS: "multiple_tabs",
  REFRESH_PAGE: "refresh_page",
  SCREEN_RECORDING_STOPPED: "screen_recording_stopped",
} as const;

export const STORAGE_KEYS = {
  REFRESH: "refresh",
  REFRESHED_SESSION_ID: "refreshedSessionId",
  BROWSER_FINGERPRINT: "browserFingerprint",
} as const;

export const LOADING_MESSAGES = {
  LOADING_ROUND: "Loading interview round, Please wait...",
  GENERATING_QUESTIONS: "Generating questions, please wait...",
  LOADING_QUESTIONS: "Loading questions, please wait...",
  SUBMITTING_ANSWER: "Submitting answer please wait...",
  RECOVERING_SESSION: "Recovering session, Please wait...",
  WRAPPING_UP: "Almost there... just wrapping things up!",
  EVALUATING: "Evaluating your responses...",
} as const;

export const NOTIFICATION_MESSAGES = {
  FAILED_TO_ANSWER: "Failed to answer question",
  TRY_AGAIN: "Please try again",
  CAMERA_ACCESS_DENIED: "Camera access denied",
  FULLSCREEN_REQUIRED: "Fullscreen mode is required for the interview",
  SESSION_EXPIRED: "Session has expired",
  ROUND_COMPLETED: "Round completed successfully",
} as const;

export const TIMER_CONSTANTS = {
  EXIT_CONFIRMATION_DELAY: 10000,
  LOADING_DELAY: 1000,
  RETRY_DELAY: 2000,
  FULLSCREEN_SETUP_DELAY: 50,
  PROCTORING_GRACE_PERIOD: 3000,
  FULLSCREEN_RETRY_DELAY: 300,
} as const;

export const UI_CONSTANTS = {
  MAX_CONTENT_WIDTH: "673px",
  CODING_ROUND_PADDING: "0 20px",
  VIDEO_RECORDER_POSITION: {
    bottom: "20px",
    right: "20px",
  },
} as const;

export const API_CONSTANTS = {
  MAX_RETRY_ATTEMPTS: 3,
  POLLING_INTERVAL: 5000,
  HEARTBEAT_INTERVAL: 30000,
} as const;

export const VALIDATION_CONSTANTS = {
  MIN_ANSWER_LENGTH: 1,
  MAX_ANSWER_LENGTH: 5000,
  REQUIRED_FIELDS: ["answer", "questionId"],
} as const;

export const ERROR_MESSAGES = {
  NO_SESSION: "No session found",
  NO_QUESTION: "No question found",
  SESSION_NOT_FOUND: "Session not found",
  QUESTIONS_NOT_GENERATED: "Questions are not generated yet",
  FULLSCREEN_NOT_SUPPORTED: "Fullscreen API is not supported on this element",
  ELEMENT_REF_NULL: "Element reference is null",
  ROUND_ALREADY_FINISHED: "Round is already finished",
  FAILED_TO_SUBMIT: "Failed to submit answer",
} as const;

export const SUCCESS_MESSAGES = {
  ANSWER_SUBMITTED: "Answer submitted successfully",
  ROUND_COMPLETED: "Round completed successfully",
  SESSION_RECOVERED: "Session recovered successfully",
} as const;

export const COMPONENT_NAMES = {
  INTERVIEW_ROUNDS: "InterviewRounds",
  INTERVIEW_ROUND_PAGE: "InterviewRoundPage",
  INTERVIEW_ROUND_CONTENT: "InterviewRoundContent",
  QUESTION_RENDERER: "QuestionRenderer",
  MCQ_QUESTION: "MCQQuestion",
  DESCRIPTIVE_QUESTION: "DescriptiveQuestion",
  CODING_ROUND: "CodingRound",
  CODE_RENDERER: "CodeRenderer",
  INSTRUCTIONS: "Instructions",
  EVALUATE: "Evaluate",
  PROGRESS_SEGMENT: "ProgressSegment",
} as const;

export type QuestionType = (typeof QUESTION_TYPES)[keyof typeof QUESTION_TYPES];
export type SessionStatus =
  (typeof SESSION_STATUS)[keyof typeof SESSION_STATUS];
export type ViolationType =
  (typeof VIOLATION_TYPES)[keyof typeof VIOLATION_TYPES];
export type ComponentName =
  (typeof COMPONENT_NAMES)[keyof typeof COMPONENT_NAMES];

export const INSTRUCTION_PANELS = [
  {
    key: "general",
    label: "General Guidelines",
    items: [
      { text: "Ensure you have a stable internet connection and a quiet, well-lit environment.", icon: "WifiOutlined" },
      { text: "The assessment must be completed in a single sitting.", icon: "ClockCircleOutlined" },
      { text: "Once started, the test will run in full-screen mode.", icon: "FullscreenOutlined" },
      { text: "Copy, paste, and cut actions are disabled.", icon: "ScissorOutlined" },
      { text: "Disable browser extensions, add-ons, and plugins that may interfere with the assessment.", icon: "ApiOutlined" },
      { text: `If you face technical issues, please contact ${qualifymeSupportEmail} immediately.`, icon: "CustomerServiceOutlined" },
    ],
  },
  {
    key: "prohibited",
    label: "Prohibited Activities",
    lead: "This assessment is proctored. Any form of external assistance or attempts to circumvent monitoring will result in disqualification.",
    items: [
      { text: "Seeking assistance from other people, AI tools, or automated solutions.", icon: "TeamOutlined" },
      { text: "Using external materials, notes, websites, or reference content.", icon: "BookOutlined" },
      { text: "Switching devices, browsers, or using multiple screens.", icon: "DesktopOutlined" },
      { text: "Attempting to bypass, disable, or interfere with proctoring measures.", icon: "SafetyOutlined" },
    ],
  }
];

export const LANGUAGE_ARRAY = [
  "javascript",
  "typescript",
  "jsx",
  "python",
  "java",
  "c",
  "cpp",
  "csharp",
  "go",
  "dart",
  "php",
  "ruby",
  "swift",
  "kotlin",
  "sql",
  "html",
  "css",
  "json",
  "bash",
  "shell",
  "yaml",
  "xml",
  "perl",
  "r",
  "rust",
];
